import { Badge, BadgeVariant } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';

interface StepTypeBadgeProps {
  type: string;
  className?: string;
}

/**
 * Format step type for display
 * Converts technical names to user-friendly labels
 */
function formatStepType(
  type: string,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const normalizedType = type.toLowerCase();

  // Handle storefront types
  if (normalizedType === 'storefront_shopify' || normalizedType === 'storefront-shopify') {
    return t('funnelsTable.stepTypes.shopifyStorefront');
  }
  if (normalizedType === 'storefront_woocommerce' || normalizedType === 'storefront-woocommerce') {
    return t('funnelsTable.stepTypes.wooCommerceStorefront');
  }

  // Handle other compound types
  if (normalizedType.includes('_')) {
    return normalizedType
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  if (normalizedType.includes('-')) {
    return normalizedType
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // Handle special cases
  const specialCases: Record<string, string> = {
    thankyou: t('funnelsTable.stepTypes.thankYou'),
    'thank-you': t('funnelsTable.stepTypes.thankYou'),
    checkout: t('funnelsTable.stepTypes.checkout'),
    landing: t('funnelsTable.stepTypes.landing'),
    offer: t('funnelsTable.stepTypes.offer'),
    upsell: t('funnelsTable.stepTypes.upsell'),
    custom: t('funnelsTable.stepTypes.custom'),
    error: t('funnelsTable.stepTypes.error'),
    external: t('funnelsTable.stepTypes.external'),
    shopify: t('funnelsTable.stepTypes.shopify'),
    woocommerce: t('funnelsTable.stepTypes.wooCommerce'),
  };

  return specialCases[normalizedType] || type.charAt(0).toUpperCase() + type.slice(1);
}

// Map step types to badge variants (matching funnel editor node colors)
const getStepTypeVariant = (stepType: string): BadgeVariant => {
  const normalizedType = stepType.toLowerCase();

  switch (normalizedType) {
    case 'landing':
      return 'blue'; // blue-500
    case 'checkout':
      return 'green'; // green-500
    case 'offer':
      return 'purple'; // purple-500
    case 'upsell':
      return 'indigo'; // indigo-500
    case 'thankyou':
    case 'thank-you':
      return 'pink'; // pink-500
    case 'custom':
      return 'gray'; // slate-500
    case 'error':
      return 'red'; // red-500
    case 'external':
      return 'orange'; // orange-500
    case 'shopify':
    case 'storefront_shopify':
    case 'storefront-shopify':
      return 'teal'; // teal-500
    case 'woocommerce':
    case 'storefront_woocommerce':
    case 'storefront-woocommerce':
      return 'violet'; // violet-500
    default:
      return 'gray';
  }
};

/**
 * Compact badge for displaying step types with consistent colors
 * Formats step type names for better readability
 */
export function StepTypeBadge({ type, className = '' }: StepTypeBadgeProps) {
  const { t } = useTranslation('common')
  const displayText = formatStepType(type, t);
  const variant = getStepTypeVariant(type);

  return (
    <Badge variant={variant} className={`h-5 px-1.5 py-0 text-xs font-normal ${className}`}>
      {displayText}
    </Badge>
  );
}
